/**
 * Admin Pricing Matrix Screen - Business-Compliant Pricing Management
 * 
 * Allows administrators to manage the pricing_matrix and entity_discount_matrix tables
 * according to business rules (only 3 revenue bands, entity-based discounts).
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Edit, Save, X, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { VALID_REVENUE_BANDS } from "../../constants/revenueBands";
import { apiRequest } from "@/lib/queryClient";

interface PricingMatrixEntry {
  id: number;
  revenue_band: string;
  revenue_range: string;
  platform_price: number;
  pillar_price: number;
}

interface EntityDiscountEntry {
  id: number;
  min_entities: number;
  max_entities: number | null;
  discount_multiplier: number;
}

export default function AdminPricingMatrixScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPricing, setEditingPricing] = useState<number | null>(null);
  const [editingEntity, setEditingEntity] = useState<number | null>(null);

  // Fetch pricing matrix
  const { data: pricingMatrix = [], isLoading: pricingLoading } = useQuery<PricingMatrixEntry[]>({
    queryKey: ['/api/admin/pricing-matrix'],
  });

  // Fetch entity discount matrix
  const { data: entityMatrix = [], isLoading: entityLoading } = useQuery<EntityDiscountEntry[]>({
    queryKey: ['/api/admin/entity-matrix'],
    select: (data: any): EntityDiscountEntry[] =>
      (data as any[]).map((e) => ({
        ...e,
        discount_multiplier:
          typeof e.discount_multiplier === 'string'
            ? parseFloat(e.discount_multiplier)
            : e.discount_multiplier,
      })),
  });

  // Update pricing matrix mutation
  const updatePricingMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<PricingMatrixEntry> }) => {
      return apiRequest(`/api/admin/pricing-matrix/${id}`, {
        method: 'PATCH',
        body: updates,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/pricing-matrix'] });
      setEditingPricing(null);
      toast({ title: "Success", description: "Pricing updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update pricing", variant: "destructive" });
    }
  });

  // Update entity matrix mutation
  const updateEntityMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<EntityDiscountEntry> }) => {
      return apiRequest(`/api/admin/entity-matrix/${id}`, {
        method: 'PATCH',
        body: updates,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/entity-matrix'] });
      setEditingEntity(null);
      toast({ title: "Success", description: "Entity discount updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update entity discount", variant: "destructive" });
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatEntityRange = (min: number, max: number | null) => {
    return max ? `${min}-${max}` : `${min}+`;
  };

  if (pricingLoading || entityLoading) {
    return <div className="p-6">Loading pricing configuration...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Pricing Matrix Configuration</h1>
        <Badge variant="outline" className="border-orange-500 text-orange-600">
          Business Rules Enforced
        </Badge>
      </div>

      {/* Business Rules Warning */}
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">Business Rules Active</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Only 3 revenue bands are supported: {VALID_REVENUE_BANDS.join(', ')}. 
                Entity discounts follow exact business multipliers. Changes affect all pricing calculations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Platform & Pillar Pricing Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Revenue Band</TableHead>
                <TableHead>Revenue Range</TableHead>
                <TableHead>Platform Price</TableHead>
                <TableHead>Pillar Price</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pricingMatrix.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <Badge variant="secondary">{entry.revenue_band}</Badge>
                  </TableCell>
                  <TableCell>{entry.revenue_range}</TableCell>
                  <TableCell>
                    {editingPricing === entry.id ? (
                      <Input
                        type="number"
                        defaultValue={entry.platform_price}
                        className="w-32"
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (value !== entry.platform_price) {
                            updatePricingMutation.mutate({
                              id: entry.id,
                              updates: { platform_price: value }
                            });
                          }
                        }}
                      />
                    ) : (
                      formatCurrency(entry.platform_price)
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPricing === entry.id ? (
                      <Input
                        type="number"
                        defaultValue={entry.pillar_price}
                        className="w-32"
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (value !== entry.pillar_price) {
                            updatePricingMutation.mutate({
                              id: entry.id,
                              updates: { pillar_price: value }
                            });
                          }
                        }}
                      />
                    ) : (
                      formatCurrency(entry.pillar_price)
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPricing === entry.id ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setEditingPricing(null)}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPricing(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingPricing(entry.id)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Entity Discount Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Entity Discount Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entity Range</TableHead>
                <TableHead>Discount Multiplier</TableHead>
                <TableHead>Effective Discount</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entityMatrix.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <Badge variant="outline">
                      {formatEntityRange(entry.min_entities, entry.max_entities)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {editingEntity === entry.id ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        defaultValue={entry.discount_multiplier}
                        className="w-24"
                        onBlur={(e) => {
                          const value = parseFloat(e.target.value);
                          if (value !== entry.discount_multiplier) {
                            updateEntityMutation.mutate({
                              id: entry.id,
                              updates: { discount_multiplier: value }
                            });
                          }
                        }}
                      />
                    ) : (
                      entry.discount_multiplier.toFixed(2)
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={entry.discount_multiplier < 1 ? "text-green-600" : "text-gray-600"}>
                      {entry.discount_multiplier === 1 
                        ? "No discount" 
                        : `${Math.round((1 - entry.discount_multiplier) * 100)}% off`
                      }
                    </span>
                  </TableCell>
                  <TableCell>
                    {editingEntity === entry.id ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setEditingEntity(null)}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingEntity(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingEntity(entry.id)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
